/**
 * TimePicker - Time picker component using select fields (hour, minute, AM/PM)
 *
 * Integrates with React Hook Form via Controller.
 * Uses 12-hour format with AM/PM selection.
 *
 * @example
 * ```tsx
 * <Controller
 *   name="startTime"
 *   control={control}
 *   render={({ field }) => (
 *     <TimePicker
 *       value={field.value || { hour: "", minute: "", period: "" }}
 *       onChange={(val) => {
 *         field.onChange(val);
 *         field.onBlur();
 *       }}
 *       error={errors.startTime}
 *     />
 *   )}
 * />
 * ```
 */

"use client";

import { Box, Flex, FormControl, FormLabel } from "@chakra-ui/react";
import { useMemo } from "react";
import { Text } from "@/components/ui";
import { useThemeTokens } from "@/lib/theme/tokens";
import { getErrorMessage } from "@/lib/utils/form";
import { CustomSelect } from "./CustomSelect";

export interface TimeValue {
  hour: string;
  minute: string;
  period: string; // "AM" or "PM"
}

// Helper functions to convert between string (HH:mm or HH:mm:ss) and TimeValue
// Minute must match option values "0".."59" (not "00"), so we normalize.
export const timeStringToValue = (
  timeStr: string | null | undefined,
): TimeValue => {
  if (!timeStr) return { hour: "", minute: "", period: "" };

  const parts = timeStr.split(":");
  if (parts.length < 2) return { hour: "", minute: "", period: "" };
  const hour24 = parseInt(parts[0], 10);
  const minuteNum = parseInt(parts[1], 10);
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  const period = hour24 >= 12 ? "PM" : "AM";
  return {
    hour: String(hour12),
    minute: Number.isNaN(minuteNum) ? "" : String(minuteNum),
    period: period,
  };
};

export const timeValueToString = (
  value: TimeValue | null | undefined,
): string => {
  if (!value || !value.hour || !value.minute || !value.period) return "";
  let hour24 = parseInt(value.hour, 10);
  if (value.period === "PM" && hour24 !== 12) {
    hour24 += 12;
  } else if (value.period === "AM" && hour24 === 12) {
    hour24 = 0;
  }
  return `${String(hour24).padStart(2, "0")}:${value.minute.padStart(2, "0")}`;
};

export interface TimePickerProps {
  /**
   * Current time value
   */
  value: TimeValue;
  /**
   * Change handler
   */
  onChange: (value: TimeValue) => void;
  /**
   * Error message
   */
  error?: { message?: string };
  /**
   * Whether field is required
   */
  required?: boolean;
  /**
   * Help text
   */
  help?: string;
  /**
   * Label for the field
   */
  label?: string;
  /**
   * Whether to show error message (default: true)
   * Set to false when error is displayed by parent component
   */
  showErrorMessage?: boolean;
  /** Disable all time selects */
  isDisabled?: boolean;
  /** Narrower selects for dense layouts (e.g. data tables) */
  compact?: boolean;
  /** Keep hour / minute / AM-PM in one row on all breakpoints (e.g. mobile cards) */
  inlineRow?: boolean;
  /**
   * Stretch hour / minute / AM-PM to fill the parent, matching DatePicker fullWidth.
   */
  fullWidth?: boolean;
}

/** Equal flex sizing for compact table layouts only. */
const TIME_FIELD_COMPACT_FLEX = "1 1 0";

/** Match DatePicker fullWidth: MM / DD flex 1, YYYY flex 1.2. */
const FULL_WIDTH_FIELD_LAYOUT = {
  hour: { flex: 1, minW: 0 },
  minute: { flex: 1, minW: 0 },
  period: { flex: 1.2, minW: 0 },
} as const;

/** Align inline-row columns with DatePicker MM / DD / YYYY. */
const INLINE_ROW_FIELD_LAYOUT = {
  hour: { flex: { base: 1, md: "none" }, w: { base: "100%", md: "100px" } },
  minute: { flex: { base: 1, md: "none" }, w: { base: "100%", md: "100px" } },
  period: { flex: { base: 1.2, md: "none" }, w: { base: "100%", md: "120px" } },
} as const;

/** Fixed select width - matches DatePicker MM/DD fields. */
const TIME_FIELD_FIXED_WIDTH = { base: "100%", md: "100px" } as const;

export const TimePicker = ({
  value,
  onChange,
  error,
  required = false,
  help,
  label,
  showErrorMessage = true,
  isDisabled = false,
  compact = false,
  inlineRow = false,
  fullWidth = false,
}: TimePickerProps) => {
  const theme = useThemeTokens();
  const selectDense = compact && !fullWidth;

  const hourOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        label: String(i + 1),
        value: String(i + 1),
      })),
    [],
  );

  const minuteOptions = useMemo(
    () =>
      Array.from({ length: 60 }, (_, i) => ({
        label: String(i).padStart(2, "0"),
        value: String(i),
      })),
    [],
  );

  const periodOptions = useMemo(
    () => [
      { label: "AM", value: "AM" },
      { label: "PM", value: "PM" },
    ],
    [],
  );

  const handleChange = (part: "hour" | "minute" | "period", val: string) => {
    onChange({
      ...value,
      [part]: val,
    });
  };

  // Extract error message from various possible error structures
  const errorMessage = getErrorMessage(
    error,
    undefined,
    ["hour", "minute", "period"],
    true,
  );
  const hasError = !!error;

  const renderTimeSelect = (
    part: "hour" | "minute" | "period",
    options: { label: string; value: string }[],
    placeholder: string,
  ) => {
    const select = (
      <CustomSelect
        options={options}
        placeholder={placeholder}
        value={value[part] || ""}
        onChange={(val) => handleChange(part, val)}
        dense={selectDense}
        formControlProps={{
          ...(fullWidth
            ? FULL_WIDTH_FIELD_LAYOUT[part]
            : compact
              ? { w: "100%", minW: 0, flex: 1 }
              : inlineRow
                ? { ...INLINE_ROW_FIELD_LAYOUT[part], minW: 0 }
                : { w: TIME_FIELD_FIXED_WIDTH }),
          isInvalid: hasError,
          isDisabled,
        }}
      />
    );

    if (compact && !fullWidth) {
      return (
        <Box flex={TIME_FIELD_COMPACT_FLEX} minW={0}>
          {select}
        </Box>
      );
    }

    return select;
  };

  return (
    <FormControl
      isInvalid={hasError}
      isDisabled={isDisabled}
      w={fullWidth ? "100%" : undefined}
    >
      {label && (
        <FormLabel
          fontSize="sm"
          fontWeight="500"
          mb="5px"
          color={theme.text.primary}
          letterSpacing="-0.01em"
        >
          {label}
          {required && (
            <Text as="span" color={theme.accent.primary} ml={1}>
              *
            </Text>
          )}
        </FormLabel>
      )}

      <Flex
        gap={compact && !fullWidth ? "6px" : "10px"}
        flexWrap={inlineRow || compact || fullWidth ? "nowrap" : "wrap"}
        w={inlineRow || compact || fullWidth ? "100%" : undefined}
        maxW="100%"
        align="stretch"
        overflowX={compact && !fullWidth ? "auto" : undefined}
        sx={
          compact && !fullWidth
            ? {
                "&::-webkit-scrollbar": { height: "4px" },
              }
            : undefined
        }
      >
        {renderTimeSelect("hour", hourOptions, "Hour")}
        {renderTimeSelect("minute", minuteOptions, "Min")}
        {renderTimeSelect("period", periodOptions, "AM/PM")}
      </Flex>

      {errorMessage && showErrorMessage && (
        <Text
          color={theme.accent.primary}
          fontSize="xs"
          fontWeight="500"
          mt={1.5}
        >
          {errorMessage}
        </Text>
      )}

      {help && !error && (
        <Text variant="tertiary" fontSize="xs" mt={1.5}>
          {help}
        </Text>
      )}
    </FormControl>
  );
};
